import { useEffect, useId, useRef, useState } from 'react';
import { useSession } from '../../state/useSession';
import { ApiError } from '../../api/client';
import { TAKEN_DOWN_MESSAGE, TakenDownError, makePrivate, relistApp } from '../../api/publish';
import { setSiteAccess } from '../../api/hosting';
import type { Project } from '@/storage/ProjectRepository';
import type { AppDefinition } from '@/types/app';
import {
  SITE_STATUS_LABEL,
  takeOfflineConfirmation,
  websiteActions,
  type WebsiteAction,
  type WebsiteActionKey,
} from '@/utils/websiteSheet';
import { Button, Chip, TextField } from '../../ui';
import { SAVED_BUT_OFFLINE, liveState, publishLatest, recordPrivate, type LivePublish } from '../../state/livePage';
import { SITE_STATUS_MEANING, type SiteCheck } from '../../state/siteStatus';

/** The actions that change the page itself; the sheet's Copy link, Open and Share live elsewhere here. */
/** Ours, for a `single_one_site` refusal that came without the server's sentence. */
const SINGLE_ONE_SITE = 'Single keeps one website live. Move to Monthly for more.';

const PAGE_ACTIONS = new Set<WebsiteActionKey>(['setPassword', 'makePublic', 'takeOffline', 'putBackOnline', 'putBackWithPassword']);

/** What is on its way to the server: a publish, or one of the page actions. */
type Acting = 'publish' | WebsiteActionKey;

/**
 * Publishing is the point of the product: it ends in a URL you bookmark.
 *
 * The sequence mirrors the phone's publishWebEdit exactly — assemble and upload, then
 * prefer the stable saddle URL and fall back to the direct upload link — and the result
 * is written back onto the project so a reload still knows where the app lives.
 *
 * Every failure names its own reason. WebAppPublishError distinguishes five, and
 * collapsing them into "publishing failed" would hide the two the person can act on
 * (not signed in, no backend).
 *
 * A chat edit publishes its own change (see ChatPanel), so "Publish changes" is not the way
 * an edit goes live any more: it is offered only when the page is BEHIND — an edit whose
 * publish failed, or a record from before edits published themselves. A button that is
 * always there says the page always needs it.
 *
 * The page's state, and the way out of it, come first, in the phone's words and with the phone's
 * choices (`websiteActions`): Live offers Set a password and Take offline, Password offers Make
 * public and Take offline, Offline offers Put it back online and Put it back with a password. The
 * owner could not tell whether their site was up, and the only way down was "Make private" at the
 * bottom of the panel, with no way back once the page was reloaded except a Publish button that
 * read like making a new site. That state is the SERVER's (`site`), never this record's: see
 * state/siteStatus.ts.
 *
 * Offline offers BOTH ways back because taking a page offline clears its password on the server
 * (`unpublishWeb`). With only Put it back online, a page that had been behind a password came
 * back public — to an owner who only meant to undo Take offline.
 */
export default function PublishPanel({
  project,
  editing = false,
  site,
  onSiteChange,
}: {
  project: Project;
  /**
   * A chat edit is being made, and will publish itself when it lands. Nothing here publishes or
   * takes the page down meanwhile: for an app with no page yet, two publishes at once register
   * it twice, and the second takes the address `<slug>-2`; and a Take offline that finishes
   * before the edit's publish is followed by that publish writing back onto the record.
   */
  editing?: boolean;
  /** Whether the page is up, as the server said when the editor asked. */
  site: SiteCheck;
  /**
   * What this panel changed the page to. Only ever from a server's answer: a publish's own
   * response, or the takedown calls having succeeded.
   */
  onSiteChange: (next: SiteCheck) => void;
}) {
  const { user } = useSession();
  const definition = project.definition as AppDefinition;
  const hostedAppId = definition.hostedAppId ?? '';
  const known = site.kind === 'known' ? site : null;
  const offline = known?.status === 'offline';
  const headingId = useId();
  const [url, setUrl] = useState<string | null>(definition.webUrl ?? null);
  /*
   * Follows the record, because this panel is no longer the only thing that publishes: a chat
   * edit can give a never-published app its address, and the link belongs here when it does.
   */
  useEffect(() => {
    setUrl(definition.webUrl ?? null);
  }, [definition.webUrl]);
  const [error, setErrorText] = useState<string | null>(null);
  /** What failed, for error_shown (analytics/errorsShown.ts): never the words, a code. */
  const [errorCode, setErrorCode] = useState('publish');
  function setError(text: string | null, code = 'publish'): void {
    setErrorText(text);
    setErrorCode(code);
  }
  /*
   * Single keeps one website live (2026-10-27): the server refuses a second with 409
   * `single_one_site` and its own sentence, which is shown as it is. A reply with only the token
   * gets ours, so nobody reads "single_one_site".
   */
  function failed(err: unknown, fallback: string, code: string): void {
    if (err instanceof ApiError && (err.code === 'single_one_site' || err.reason === 'single_one_site')) {
      const said = err.reason && err.reason !== 'single_one_site' ? err.reason : SINGLE_ONE_SITE;
      setError(said, 'publish_single_one_site');
      return;
    }
    setError(err instanceof Error ? err.message : fallback, code);
  }
  /*
   * One thing at a time, and the button that started it is the one that spins. Two at once is
   * two writes to the page racing — a password set while a Put it back online lands, say — and
   * whichever answered second would decide what the page is.
   */
  const [acting, setActing] = useState<Acting | null>(null);
  const busy = acting !== null;
  const [copied, setCopied] = useState(false);
  const [takenDown, setTakenDown] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const passwordDialog = useRef<HTMLDialogElement>(null);
  const passwordTitleId = useId();
  /*
   * Which action the password is for — a live page's Set a password, or an offline page's way
   * back — and null while the dialog is closed. Its form exists only while it is open: a password
   * field sitting hidden in the page is one a password manager may fill, and one more "Password"
   * beside the status word of the same name.
   */
  const [passwordFor, setPasswordFor] = useState<'setPassword' | 'putBackWithPassword' | null>(null);
  const [password, setPassword] = useState('');

  /**
   * Assemble on the server, then host — the ONE content-update path, which a chat edit takes
   * too (state/livePage.ts). Shared by Publish, Publish changes and Put it back online.
   */
  async function publishContent(
    opts: { putOnline?: boolean; password?: string } = {},
    relisted = false,
  ): Promise<LivePublish> {
    /*
     * "Publish changes" is a content update, which keeps a page its owner took offline offline
     * and a password page behind its password; sending 'public' there re-opened both on every
     * edit. So a visibility is named only when the person asked for the page to be UP.
     *
     * AppPublished is counted inside publishLatest, from the record as stored, so an app a chat
     * edit put online first is not counted a second time here.
     */
    const published = await publishLatest(project.id, user?.id ?? '', opts);
    setUrl(published.siteUrl);
    /*
     * The server's answer, so the status can follow it: who may open the page is what the server
     * SAYS after this publish, and only an explicit false is a page that is off. From a server too
     * old to say, a content update keeps a password it had; and a page put up with one reads as
     * Live until the server is asked again — never Password on the strength of a button pressed,
     * which would be telling an owner their page is private when that is not known.
     */
    // Unasked (a first publish), it is not in Discover: every app registered here is created
    // unlisted. The editor asks the server again as soon as the app has an id to ask with.
    const listed = relisted || (known?.listed ?? false);
    onSiteChange(
      published.webPublished === false
        ? { kind: 'known', status: 'offline', listed, takenDown: false }
        : {
            kind: 'known',
            status: published.webVisibility
              ? (published.webVisibility === 'password' ? 'password' : 'live')
              : !opts.putOnline && known?.status === 'password' ? 'password' : 'live',
            listed,
            takenDown: false,
            siteUrl: published.siteUrl,
          },
    );
    return published;
  }

  /**
   * Publish, and when the server answers that the app is private, relist it and put it up.
   *
   * The server switches on only a page that is not private (`unlistedAt`), and relisting is the
   * one way to clear that. An app Take offline found already off Discover is not made private
   * (see makePrivate), so for those this goes straight through and never lists them.
   */
  async function publishOrRelist(putOnline: boolean, password?: string): Promise<LivePublish> {
    const opts = { putOnline, ...(password ? { password } : {}) };
    try {
      return await publishContent(opts);
    } catch (err) {
      if (!(err instanceof ApiError && err.code === 'unlisted')) throw err;
      await relistApp(hostedAppId);
      return publishContent({ ...opts, putOnline: true }, true);
    }
  }

  /** The server's refusal of an app a moderator took down: not something a retry can fix. */
  function isTakenDown(err: unknown): boolean {
    return err instanceof TakenDownError
      || (err instanceof ApiError && (err.code === 'taken_down' || err.takenDown === true));
  }

  function showTakenDown(err: unknown): void {
    setTakenDown(true);
    // The 403's generic message says nothing; the server's sentence points at support.
    setError(err instanceof ApiError && err.reason ? err.reason : new TakenDownError().message, 'site_taken_down');
  }

  async function publish(): Promise<void> {
    setError(null);
    setActing('publish');
    setNotice(null);
    /*
     * With no link on record the button says "Publish", and pressing it is a decision to put
     * the page UP. With one it says "Publish changes": the content changes, and whether the
     * page is on stays where "Make private" or "Take offline" left it.
     */
    const putOnline = !url;
    try {
      /*
       * With the server unreachable, a private app offers Publish rather than Put it back
       * online, and pressing it means the same: relist, then put the page up.
       */
      const result = await publishOrRelist(putOnline);
      if (result.webPublished === false) {
        setNotice(SAVED_BUT_OFFLINE);
      }
    } catch (err) {
      if (isTakenDown(err)) showTakenDown(err);
      else failed(err, 'Could not publish this app.', 'publish');
    } finally {
      // Always: a stuck spinner is the failure people actually report.
      setActing(null);
    }
  }

  /**
   * Put a page that is offline back up, at the address it had — public, or behind a password.
   *
   * Named explicitly: a content update would leave the page exactly as Take offline left it —
   * off. And it must not report success unless the server says the page is up, nor say it is
   * behind a password unless the server says that.
   */
  async function putBackOnline(withPassword?: string): Promise<void> {
    setError(null);
    setNotice(null);
    setActing(withPassword ? 'putBackWithPassword' : 'putBackOnline');
    try {
      const result = await publishOrRelist(true, withPassword);
      setNotice(
        result.webPublished === false
          ? SAVED_BUT_OFFLINE
          : result.webVisibility === 'password'
            ? 'Your website is back online, behind a password, at the same address.'
            : 'Your website is back online, at the same address.',
      );
    } catch (err) {
      if (isTakenDown(err)) {
        // Not a failure they can retry, so the retry affordance goes away with it.
        showTakenDown(err);
      } else {
        failed(err, 'Your website could not be put back online.', 'publish_put_back');
      }
    } finally {
      setActing(null);
    }
  }

  /**
   * Set a password on a page that is up, or take it away — the phone's Set a password and Make
   * public. Only who may open the page changes: no document is sent (see `setSiteAccess`), so a
   * page that is behind this version stays behind, and says so, until Publish changes.
   *
   * The status follows the server's answer, and nothing else: a page shown as Password must be
   * one the server confirmed is behind a password.
   */
  async function changeAccess(access: { visibility: 'public' } | { visibility: 'password'; password: string }): Promise<void> {
    setError(null);
    setNotice(null);
    setActing(access.visibility === 'password' ? 'setPassword' : 'makePublic');
    try {
      const result = await setSiteAccess(hostedAppId, access);
      const siteUrl = result.siteUrl ?? known?.siteUrl;
      onSiteChange({
        kind: 'known',
        status: result.webVisibility === 'password' ? 'password' : 'live',
        listed: known?.listed ?? false,
        takenDown: false,
        ...(siteUrl ? { siteUrl } : {}),
      });
      setNotice(
        result.webVisibility === 'password'
          ? 'Done — visitors need the password to open your website now.'
          : 'Done — anyone with the link can open your website now.',
      );
    } catch (err) {
      if (isTakenDown(err)) showTakenDown(err);
      else failed(err, 'That could not be changed just now. Try again.', 'publish_access');
    } finally {
      setActing(null);
    }
  }

  /** Ask for the password first: the phone's "Set a password" prompt, as a dialog. */
  function askPassword(key: 'setPassword' | 'putBackWithPassword'): void {
    setPasswordFor(key);
    setPassword('');
    passwordDialog.current?.showModal();
  }

  function closePassword(): void {
    passwordDialog.current?.close();
    setPasswordFor(null);
    setPassword('');
  }

  function submitPassword(): void {
    // Sent as typed: a space is a character somebody may have meant. Only empty is no password.
    if (!password || !passwordFor) return;
    const chosen = password;
    const key = passwordFor;
    closePassword();
    if (key === 'setPassword') void changeAccess({ visibility: 'password', password: chosen });
    else void putBackOnline(chosen);
  }

  async function copy(): Promise<void> {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard permission denied — the link is on screen and selectable anyway */
    }
  }

  /**
   * Take the page off the internet — and out of Discover, when it is in it.
   *
   * The local record is updated ONLY after the server confirms, and never on failure: a
   * project that says "offline" over a page that is still up is the exact bug this exists
   * to prevent, and it is worse than an error message.
   */
  async function takeOffline(): Promise<void> {
    dialog.current?.close();
    setError(null);
    setNotice(null);
    setActing('takeOffline');
    try {
      await makePrivate(hostedAppId);
      // Onto the record as stored, not this screen's copy of it: see recordPrivate.
      await recordPrivate(project.id, user?.id ?? '');
      setUrl(null);
      // Out of Discover either way: makePrivate delists it unless the server said it was not in it.
      onSiteChange({ kind: 'known', status: 'offline', listed: false, takenDown: false });
      setNotice('Your website is offline. Your app and everything people saved in it stay as they are.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Your website could not be taken offline.', 'publish_take_offline');
    } finally {
      setActing(null);
    }
  }

  /*
   * With no page, "Publish". With one, "Publish changes" only when the page does not have this
   * version — or when this record is too old to say, since hiding the button then could strand
   * an edit that never went live. And never while an edit is on its way: it publishes itself.
   *
   * Neither while the server says the page is offline: Put it back online is the one action
   * then, and it publishes this version as it puts the page up. Two buttons that both publish
   * made the owner choose between words for one thing.
   */
  const refused = takenDown || Boolean(known?.takenDown);
  const live = liveState(definition);
  const offerPublish = !editing && !refused && !offline && (!url || live !== 'current');
  /*
   * Take offline whenever the page is not known to be down. While the server has not answered,
   * the record's address is enough to offer it: taking a page down is the safe direction, and
   * makePrivate asks the server itself and acts on both halves when it cannot.
   */
  const offerTakeOffline = Boolean(hostedAppId) && !refused && (known ? !offline : Boolean(url));
  /*
   * With the server's answer, whatever the phone's sheet offers for that state that changes the
   * page — each state's way out, in the same words. Without one, only Take offline.
   */
  const pageActions: WebsiteAction[] = !hostedAppId || refused
    ? []
    : known
      ? websiteActions(known.status).filter((a) => PAGE_ACTIONS.has(a.key))
      : offerTakeOffline
        ? websiteActions('live').filter((a) => a.key === 'takeOffline')
        : [];
  const confirmation = takeOfflineConfirmation(project.name);

  function runAction(key: WebsiteActionKey): void {
    if (key === 'takeOffline') dialog.current?.showModal();
    else if (key === 'putBackOnline') void putBackOnline();
    else if (key === 'makePublic') void changeAccess({ visibility: 'public' });
    else if (key === 'setPassword' || key === 'putBackWithPassword') askPassword(key);
  }

  return (
    <section className="publish-panel" aria-labelledby={headingId}>
      <h2 id={headingId} className="type-micro section-label">Your website</h2>
      {(known || offerTakeOffline || site.kind === 'unknown') && (
        <div className="publish-state">
          {known && (
            <p className="type-body publish-state-text">
              <Chip
                label={SITE_STATUS_LABEL[known.status]}
                tone={known.status === 'live' ? 'success' : known.status === 'password' ? 'primary' : 'neutral'}
              />{' '}
              <span className="type-caption">{SITE_STATUS_MEANING[known.status]}</span>
            </p>
          )}
          {site.kind === 'unknown' && (
            // No status word from a failed lookup: a guess here is a claim about who can open it.
            <p className="type-caption publish-state-text">We couldn&rsquo;t check whether your website is online just now.</p>
          )}
          {/*
            * All wait for an edit on its way, and each for the others. Whichever lands second
            * writes onto the record after the other, and a page taken down under a publish that
            * then wrote its address back read as live while serving nothing.
            */}
          {pageActions.map((action) => (
            <Button
              key={action.key}
              title={action.label}
              variant={action.destructive ? 'dangerSoft' : action.key === 'putBackOnline' ? 'primary' : 'secondary'}
              loading={acting === action.key}
              disabled={editing || (busy && acting !== action.key)}
              onPress={() => runAction(action.key)}
            />
          ))}
        </div>
      )}
      {known?.takenDown && !takenDown && (
        <p role="alert" data-error-code="site_taken_down" className="type-caption field-error-text">{TAKEN_DOWN_MESSAGE}</p>
      )}
      {url && !offline && (
        <p className="type-body publish-url">
          <a href={url} target="_blank" rel="noreferrer">{url}</a>
        </p>
      )}
      {url && offerPublish && live === 'behind' && (
        <p className="type-caption">The live page doesn't have your latest change yet.</p>
      )}
      {url && offerPublish && live === 'unknown' && (
        <p className="type-caption">The live page may be older than this version.</p>
      )}
      {error && <p role="alert" data-error-code={errorCode} className="type-caption field-error-text">{error}</p>}
      {notice && <p role="status" className="type-caption">{notice}</p>}
      <div className="publish-actions">
        {offerPublish && (
          <Button
            title={url ? 'Publish changes' : 'Publish'}
            variant={url ? 'secondary' : 'primary'}
            onPress={() => void publish()}
            loading={acting === 'publish'}
            disabled={busy && acting !== 'publish'}
          />
        )}
        {url && !offline && <Button title={copied ? 'Copied' : 'Copy link'} variant="ghost" onPress={() => void copy()} />}
      </div>

      <dialog ref={dialog} aria-label={confirmation.title} className="confirm-dialog">
        <h2 className="type-title">{confirmation.title}</h2>
        {/*
          * The phone's words, and only those. It used to add "It comes off Discover too" for a
          * listed app — but Discover is switched off on both clients, so that named a place the
          * owner cannot see. Taking the page down still delists it (makePrivate); it just is not
          * something to explain here.
          */}
        <p className="type-body">{confirmation.body}</p>
        <div className="build-actions">
          <Button title={confirmation.confirm} variant="dangerSoft" onPress={() => void takeOffline()} />
          <Button title="Cancel" variant="ghost" onPress={() => dialog.current?.close()} />
        </div>
      </dialog>

      <dialog
        ref={passwordDialog}
        aria-labelledby={passwordTitleId}
        className="confirm-dialog"
        // Escape closes a modal dialog without going through Cancel; forget what was typed then too.
        onClose={() => {
          setPasswordFor(null);
          setPassword('');
        }}
      >
        {passwordFor && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitPassword();
            }}
          >
            <h2 id={passwordTitleId} className="type-title">Set a password</h2>
            <p className="type-body">
              {passwordFor === 'setPassword'
                ? 'Visitors will need it to open your website. Share it with the people who should see it.'
                : 'Your website comes back at the same address, and visitors will need this password to open it.'}
            </p>
            <TextField label="Password" type="password" value={password} onChangeText={setPassword} autoFocus />
            <div className="build-actions">
              <Button
                type="submit"
                title={passwordFor === 'setPassword' ? 'Set a password' : 'Put it back with a password'}
                disabled={!password}
              />
              <Button title="Cancel" variant="ghost" onPress={closePassword} />
            </div>
          </form>
        )}
      </dialog>
    </section>
  );
}
